<template>
  <div>
    <template v-if="store.isMortgage">
      <v-container fluid>
        <div class="pb-0 px-2 d-flex align-center">
          <v-checkbox v-model="isAdditionalInfoHidden" :true-value="false" :false-value="true"></v-checkbox>
          {{ $i18n.usePrintout.PRINT_ADDITIONAL_INFO }}
        </div>
      </v-container>
    </template>
    <v-container fluid grid-list-lg>
      <v-treeview
        v-model="selectedItems"
        :items="items"
        :open="open"
        item-disabled="locked"
        item-key="key"
        selectable
        @input="onSettingsChange"
      >
        <template v-slot:label="{ item }">
          {{ item.header() }}
        </template>
      </v-treeview>
    </v-container>
  </div>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, PropType, ref, SetupContext} from 'vue';
import {
  OfferDataTableElementKey,
  OfferDataTableGroupKey
} from '@/components/calculator/results/offers/OfferTableElement';
import {
  OfferTableElementPrintout,
  OfferTableElementPrintoutKey,
  usePrintoutSettings
} from '@/components/calculator/printout/usePrintoutSettings';
import {SimulationProductType, useSimulationStore} from '@/components/calculator/services/SimulationStore';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {debouncedAtInput} from '@/services/utils/BasicUtils';
import EnvironmentService from '@/env/EnvironmentService';

export default defineComponent({
  props: {
    productType: {
      type: String as PropType<SimulationProductType>,
      required: true,
    },
  },
  setup(props, context: SetupContext) {
    const ELEMENTS_OPEN_BY_DEFAULT: readonly OfferTableElementPrintoutKey[] = [
      OfferDataTableGroupKey.ROOT,
      OfferDataTableGroupKey.BASIC_PARAMETERS_GROUP,
    ] as const;

    const mounted = ref<boolean>(false);
    const store = useSimulationStore();
    const {
      elements,
      getPrintoutSettings,
      savePrintoutSettings,
      IS_ADDITIONAL_INFO_HIDDEN_DEFAULT_VALUE,
    } = usePrintoutSettings(props.productType);

    const open = ref<Array<OfferTableElementPrintoutKey>>([...ELEMENTS_OPEN_BY_DEFAULT,]);

    const items = computed<OfferTableElementPrintout[]>(() => {
      let children: OfferTableElementPrintout[];
      if (EnvironmentService.Environment.isAppDomain().NESTO_RO) {
        const dti: OfferTableElementPrintout = elements.value.find(item => item.key === OfferDataTableElementKey.DTI)!;
        children = [...elements.value.filter(element => element.key !== OfferDataTableElementKey.DTI),];
        if (dti) {
          dti.open = false;
          children.unshift(dti);
        }
      } else {
        children = elements.value;
      }
      return [
        {
          key: OfferDataTableGroupKey.ROOT,
          header: () => I18NGetter().usePrintout.ALL,
          children,
        },
      ];
    });

    const selectedItems = ref<OfferTableElementPrintoutKey[]>([]);
    const isAdditionalInfoHidden = ref<boolean>(IS_ADDITIONAL_INFO_HIDDEN_DEFAULT_VALUE);

    const getSettings = async() => {
      const settings = await getPrintoutSettings();
      selectedItems.value = settings.selectedItems;
      isAdditionalInfoHidden.value = settings.isAdditionalInfoHidden;
    };

    const onSettingsChange = debouncedAtInput(() => {
      if (mounted.value) {
        savePrintoutSettings(selectedItems.value, isAdditionalInfoHidden.value);
      }
    });

    onMounted(() => {
      getSettings();
      mounted.value = true;
    });

    return {
      items,
      selectedItems,
      isAdditionalInfoHidden,
      onSettingsChange,
      open,
      store,
    };
  },
});
</script>
